import type { ContainerNode, LayoutNode } from "../layout-types";

export type PastedNodeParentLayout = "flex" | "absolute";

type BehaviorLike = {
  nodes?: Array<{ id: string }>;
  connections?: Array<{
    id: string;
    fromNodeId: string;
    toNodeId: string;
  }>;
};

function cloneBehaviorWithFreshIds(
  behavior: LayoutNode["behavior"],
): LayoutNode["behavior"] {
  if (!behavior) return behavior;
  const behaviorClone = structuredClone(behavior) as BehaviorLike;
  const nodes = behaviorClone.nodes ?? [];
  const nodeIdMap = new Map<string, string>();
  const nextNodes = nodes.map((node) => {
    const nextId = crypto.randomUUID();
    nodeIdMap.set(node.id, nextId);
    return {
      ...node,
      id: nextId,
    };
  });
  const nextConnections = (behaviorClone.connections ?? []).map(
    (connection) => ({
      ...connection,
      id: crypto.randomUUID(),
      fromNodeId: nodeIdMap.get(connection.fromNodeId) ?? connection.fromNodeId,
      toNodeId: nodeIdMap.get(connection.toNodeId) ?? connection.toNodeId,
    }),
  );
  return {
    ...behaviorClone,
    nodes: nextNodes,
    connections: nextConnections,
  } as LayoutNode["behavior"];
}

/**
 * Deep-clones a layout node tree while regenerating unique IDs for:
 * - all layout nodes recursively
 * - all behavior nodes
 * - all behavior connections
 */
export function cloneNodeWithFreshIds(node: LayoutNode): LayoutNode {
  const cloned = structuredClone(node) as LayoutNode;
  const assignIdsRecursively = (current: LayoutNode): LayoutNode => {
    const nextId = crypto.randomUUID();
    const nextBehavior = cloneBehaviorWithFreshIds(current.behavior);
    if (current.kind !== "container") {
      return {
        ...current,
        id: nextId,
        behavior: nextBehavior,
      };
    }
    return {
      ...current,
      id: nextId,
      behavior: nextBehavior,
      children: current.children.map((child) => assignIdsRecursively(child)),
    };
  };
  return assignIdsRecursively(cloned);
}

export const isRelativePositionedNode = (node: LayoutNode): boolean =>
  typeof node.style?.position === "string" &&
  node.style.position.toLowerCase() === "relative";

/**
 * A relatively positioned container nested in a flex parent must use flex layout
 * so its children can participate in the flex flow.
 */
export const requiresFlexContainerLayout = (
  node: LayoutNode,
  parentContainerLayout: PastedNodeParentLayout | null,
): boolean =>
  node.kind === "container" &&
  parentContainerLayout === "flex" &&
  isRelativePositionedNode(node);

const forceAbsolutePosition = (node: LayoutNode): LayoutNode => {
  if (!isRelativePositionedNode(node)) return node;
  return {
    ...node,
    style: {
      ...(node.style ?? {}),
      position: "absolute",
    },
  };
};

const normalizePastedNodeForParentLayout = (
  node: LayoutNode,
  parentLayout: PastedNodeParentLayout,
): LayoutNode => {
  const normalized =
    parentLayout === "flex" ? node : forceAbsolutePosition(node);

  if (normalized.kind !== "container") {
    return normalized;
  }

  const container = normalized as ContainerNode;
  const layout: ContainerNode["layout"] =
    parentLayout === "flex" && isRelativePositionedNode(container)
      ? "flex"
      : container.layout;
  const childParentLayout: PastedNodeParentLayout =
    layout === "flex" ? "flex" : "absolute";

  return {
    ...container,
    layout,
    children: container.children.map((child) =>
      normalizePastedNodeForParentLayout(child, childParentLayout),
    ),
  };
};

/**
 * Normalizes a node tree for its immediate container parent layout.
 * `position: relative` is only kept when the parent container uses flex layout.
 */
export function normalizeNodeForContainerParent(
  node: LayoutNode,
  containerLayout: ContainerNode["layout"],
): LayoutNode {
  return normalizePastedNodeForParentLayout(
    node,
    containerLayout === "flex" ? "flex" : "absolute",
  );
}

/**
 * Normalizes pasted nodes so `position: relative` is only kept under a flex parent.
 * Pasting at scene root or into a non-flex container forces absolute positioning.
 */
export function normalizePastedNodesForParentLayout(
  nodes: LayoutNode[],
  parentLayout: PastedNodeParentLayout,
): LayoutNode[] {
  return nodes.map((node) =>
    normalizePastedNodeForParentLayout(node, parentLayout),
  );
}
